import { setCookieValue, trackEvent } from '@do/seashell';
import { useQuery } from '@do/seashell/graphql';
import { useLocation } from '@do/seashell/navigation';
import { Box, style } from '@do/walrus';
import _get from 'lodash/get';
import React, { useEffect, useMemo } from 'react';
import {
  APP_PLATFORM_TEMPLATE_COOKIE,
  CHURN_SURVEY_URL,
  GITHUB_STUDENT_PACK,
  ONBOARDING_ORIGIN_PARAM
} from '../constants';
import copy from '../copy';
import { GetLoginInfo } from '../generated-types/GetLoginInfo';
import {
  registerReferralCode,
  registerReferralCodeVariables
} from '../generated-types/registerReferralCode';
import useActivationRedirectCookie from '../hooks/use-activation-redirect-cookie';
import useDocumentTitle from '../hooks/use-document-title';
import useGithubStudentCookie from '../hooks/use-github-student-cookie';
import useLoginVariants from '../hooks/use-login-variants';
import useOneTimeCookie from '../hooks/use-one-time-cookie';
import { LoginType } from '../interfaces/login';
import { DynamicVariantProps, VariantProps } from '../interfaces/variant';
import {
  cloudwaysVariant,
  githubLoginVariant,
  managedKafkaVariant,
  paperspaceVariant,
  premCpuVariant,
  premiumGpDroplets,
  scalableStorageVariant,
  whatIsNewVariant
} from '../login-variants';
import {
  GET_LOGIN_FORM_DATA,
  REGISTER_REFERRAL_CODE
} from '../queries/queries';
import { getRefcode } from '../utils/get-refcode';
import { getSessionData } from '../utils/gql-utils';
import { getQueryParam } from '../utils/query-utils';
import { getRandomWeightedVariant } from '../utils/variant-utils';
import { BannerType, HeaderBanner, Loading, Wrap, WrapSplit } from './Common';
import { DynamicVariant } from './DynamicVariant';
import ErrorBanner from './ErrorBanner';
import FormWrap from './FormWrap';
import Header from './Header';
import LoginForm from './LoginForm';
import { StepTransitionManager } from './StepTransitionManager';
import TwoFactorAuthForm from './TwoFactorAuthForm';
import { Variant } from './Variant';
import VerificationForm from './VerificationForm';

const APP_PLATFORM_TEMPLATE_URL_REGEX =
  /^https:\/\/cloud.digitalocean.com\/apps\/new\?(template|repo)=https:\/\/github\.com/;

const STEPS = {
  login: 0,
  two_factor_auth: 1,
  two_factor_auth_secondary: 2,
  device_verification: 3
};

let variant: VariantProps | DynamicVariantProps;

const randomVariant =
  getRandomWeightedVariant([
    { weight: 0, variant: cloudwaysVariant },
    { weight: 0, variant: premCpuVariant },
    { weight: 1, variant: paperspaceVariant },
    { weight: 0, variant: whatIsNewVariant },
    { weight: 1, variant: managedKafkaVariant },
    { weight: 1, variant: scalableStorageVariant },
    { weight: 1, variant: premiumGpDroplets }
  ]) || paperspaceVariant;

const WrapComponentContent: React.FC<{
  children: React.ReactNode;
  step: number;
}> = ({ children, step }) => {
  const WrapComponent = variant?.splash ? WrapSplit : Wrap;

  return (
    <WrapComponent>
      <FormWrap style={{ paddingBottom: `${style.vars.space['3']}` }}>
        <StepTransitionManager currentStepNum={step}>
          <Box px={style.vars.whitespace.medium} display="block">
            {children}
          </Box>
        </StepTransitionManager>
      </FormWrap>
    </WrapComponent>
  );
};

const Login: React.FC = () => {
  const { loginVariant, hasError } = useLoginVariants();
  const location = useLocation();
  const { search } = location;
  const refcode = getRefcode(search);
  useQuery<registerReferralCode, registerReferralCodeVariables>(
    REGISTER_REFERRAL_CODE,
    {
      variables: { refcode },
      skip: !refcode
    }
  );

  useActivationRedirectCookie();
  const onboardingOrigin = getQueryParam(ONBOARDING_ORIGIN_PARAM, search);

  const { loading: isLoading, data } = useQuery<GetLoginInfo>(
    GET_LOGIN_FORM_DATA,
    {
      variables: {
        onboarding_origin: onboardingOrigin,
        redirect_url: getQueryParam('redirect_url', search)
      }
    }
  );

  const isGitHubStudentVariant = onboardingOrigin === GITHUB_STUDENT_PACK;
  useGithubStudentCookie(isGitHubStudentVariant);

  const isUserAccountDeactivated = useOneTimeCookie(
    '_digitalocean_flash_code',
    'user_account_deactivated'
  );

  const [step, setStep] = React.useState(STEPS.login);
  const [config2fa, setConfig2fa] = React.useState<LoginType>();

  useDocumentTitle(copy.form.login_title);

  const shouldRenderFromJSON =
    loginVariant && !hasError && !isGitHubStudentVariant;

  const variant = useMemo(() => {
    if (shouldRenderFromJSON) return loginVariant;
    if (isGitHubStudentVariant) return githubLoginVariant;

    return randomVariant;
  }, [shouldRenderFromJSON, isGitHubStudentVariant, loginVariant]);

  const preferredLoginState = _get(location, 'state.preferredLoginState');
  const user = _get(data, 'loginFormData.user');

  const hasPreferredLogin =
    preferredLoginState !== 'none' &&
    !isLoading &&
    !!_get(user, 'email') &&
    !!_get(user, 'sign_in_method') &&
    !!_get(user, 'avatar_url');

  /**
   * This handles the case of a new-to-DO user clicking on a "Deploy to DO"
   * button elsewhere on the internet.
   *
   * - The user clicks on the button and is directed to a URL like this:
   *     https://cloud.digitalocean.com/apps/new?template=https://github.com/RocketChat/Rocket.Chat/tree/master
   * - The Edge gateway detects that the user is not logged in and redirects
   *   them to the login form with a `redirect_url` param like this:
   *     https://cloud.digitalocean.com/login?redirect_url=https%3A%2F%2Fcloud.digitalocean.com%2Fapps%2Fnew%3Ftemplate%3Dhttps%3A%2F%2Fgithub.com%2FRocketChat%2FRocket.Chat%2Ftree%2Fmaster
   * - We detect that the `redirect_url` param is a "Deploy to DO" URL.
   * - We set a cookie with the complete redirect URL.
   *
   * Later on in the signup flow, once the user's account is successfully
   * activated, the onboarding app will use this cookie to redirect the user
   * back to the "Deploy to DO" create flow in the App Platform UI.
   */
  useEffect(() => {
    const redirectUrl = getQueryParam('redirect_url', search);

    if (!redirectUrl || !APP_PLATFORM_TEMPLATE_URL_REGEX.test(redirectUrl)) {
      return;
    }

    setCookieValue(APP_PLATFORM_TEMPLATE_COOKIE, redirectUrl);
  });

  useEffect(() => {
    if (isLoading) return;

    trackEvent('Login Variant', {
      variantType: _get(variant, 'type')
    });
  }, [isLoading, variant]);

  if (isLoading) {
    return <Loading isLogin={true} />;
  }

  const googleOauthUrl = `/sessions/google${search}`;
  const githubOauthUrl = `/sessions/github${search}`;

  const oauthError = _get(data, 'loginFormData.oauth_error');
  const userUUID = getSessionData('ajs_user_id').replace(/(^"|"$)/g, '');
  const churnSurvey = userUUID
    ? `${CHURN_SURVEY_URL}?uuid=${userUUID}`
    : CHURN_SURVEY_URL;

  let form;

  switch (step) {
    case STEPS.login:
      form = (
        <>
          {oauthError && (
            <ErrorBanner
              error={copy.errors[oauthError] || copy.errors.unknown}
            />
          )}
          <LoginForm
            googleOauthUrl={googleOauthUrl}
            githubOauthUrl={githubOauthUrl}
            hasPreferredLogin={hasPreferredLogin}
            user={user}
            team={_get(data, 'loginFormData.team')}
            passwordSuccessBanner={_get(location, 'state.success')}
            showNext={(nextStep: LoginType) => {
              if (STEPS[nextStep.type] === STEPS.two_factor_auth) {
                setConfig2fa(nextStep);
              }
              setStep(STEPS[nextStep.type]);
            }}
            variant={variant}
          />
        </>
      );
      break;
    case STEPS.two_factor_auth:
      form = config2fa && (
        <TwoFactorAuthForm
          key={step}
          changeStep={() => setStep(STEPS.two_factor_auth_secondary)}
          type={config2fa.primary}
          nextStepType={config2fa.secondary}
        />
      );

      break;
    case STEPS.two_factor_auth_secondary:
      form = config2fa && config2fa.secondary && (
        <TwoFactorAuthForm
          key={step}
          changeStep={() => setStep(STEPS.two_factor_auth)}
          type={config2fa.secondary}
          nextStepType={config2fa.primary}
          isSecondary
        />
      );
      break;
    case STEPS.device_verification:
      form = <VerificationForm goBack={() => setStep(STEPS.login)} />;
      break;
  }

  return (
    <>
      <Header isLogin={true} queryString={search} />
      {isUserAccountDeactivated && (
        <HeaderBanner
          type={BannerType.Success}
          text={copy.deactivation_message}
          linkText={copy.survey_text}
          url={churnSurvey}
        />
      )}

      {shouldRenderFromJSON ? (
        <DynamicVariant variant={variant as DynamicVariantProps}>
          <WrapComponentContent step={step}>{form}</WrapComponentContent>
        </DynamicVariant>
      ) : (
        <Variant variant={variant as VariantProps}>
          <WrapComponentContent step={step}>{form}</WrapComponentContent>
        </Variant>
      )}
    </>
  );
};

export default Login;
